/**
 * DataProviderFactory
 * Factory pattern for creating IDataProvider instances
 */

import type { IDataProvider } from '@/core/interfaces'
import { MockDataProvider } from '@/providers/mock'
import { LocalStorageProvider } from '@/providers/local'

export type ProviderType = 'mock' | 'local' | 'api'

export interface DataProviderConfig {
  type: ProviderType
  options?: {
    // Mock provider options
    simulateDelay?: boolean

    // API provider options (future)
    apiUrl?: string
    apiKey?: string
  }
}

/**
 * Factory class for creating data provider instances
 */
export class DataProviderFactory {
  /**
   * Create a data provider based on type
   * @param type - The type of provider to create
   * @param config - Optional configuration
   * @returns IDataProvider instance
   */
  static create(type: ProviderType, config?: DataProviderConfig['options']): IDataProvider {
    switch (type) {
      case 'mock':
        return new MockDataProvider(config?.simulateDelay ?? true)

      case 'local':
        return new LocalStorageProvider()

      case 'api':
        throw new Error('API provider not yet implemented')

      default:
        throw new Error(`Unknown provider type: ${type}`)
    }
  }

  /**
   * Create a provider from configuration object
   * @param config - Provider configuration
   * @returns IDataProvider instance
   */
  static createFromConfig(config: DataProviderConfig): IDataProvider {
    return DataProviderFactory.create(config.type, config.options)
  }

  /**
   * Get the default provider based on environment
   * @returns IDataProvider instance
   */
  static createDefault(): IDataProvider {
    // In development, use mock data with delay
    if (import.meta.env.DEV) {
      return new MockDataProvider(true)
    }

    // In production, use local storage
    return new LocalStorageProvider()
  }

  /**
   * Get provider type from environment variable
   * @returns ProviderType
   */
  static getProviderTypeFromEnv(): ProviderType {
    const envType = import.meta.env.VITE_DATA_PROVIDER as ProviderType | undefined
    return envType || 'mock'
  }
}
